<template>
    <b-modal
        ref="dialog"
        title-tag="h2"
        :title="isEditMode ? 'Edit Communication' : 'Log Communication'"
        :lazy="true"
        @hidden="close"
        no-enforce-focus
        no-close-on-backdrop
        no-close-on-esc
    >
        <b-form-group label-cols="3" label="With:" label-for="with">
            <!-- do not change the module of vet select: logic handling issue -->
            <vet-select
                :showMenu="false"
                v-model="receiver"
                module="client-all-contacts"
                placeholder="Communicated with (required)"
                :class="{
                    'border-danger': formError.includes('receiver')
                }"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Date:" label-for="date">
            <date-picker
                :config="{ format: getDateFormat() }"
                v-model="date"
                placeholder="Date (required)"
                v-validate="'required'"
                name="date"
                :class="{
                    'border-danger': formError.includes('date')
                }"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Time:" label-for="time">
            <time-picker
                v-model="time"
                placeholder="Time (required)"
                v-validate="'required'"
                name="time"
                format="moment"
                :class="{
                    'form-control': true,
                    'border-danger': formError.includes('time')
                }"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Type:" label-for="type">
            <b-form-select
                v-model="type"
                :options="typeOptions"
                value-field="id"
                text-field="value"
            ></b-form-select>
        </b-form-group>

        <b-form-group
            label-cols="3"
            label="Message:"
            label-for="message"
        ></b-form-group>

        <div v-if="type === 4">
            <tinymce
                :init="tinymceConfig"
                v-model="message"
                placeholder="Communication (required)"
            />
        </div>
        <editable
            id="message"
            v-else
            v-model="message"
            placeholder="Communication (required)"
            :defaultHeight="146"
            :class="{
                'border-danger': formError.includes('message')
            }"
        />

        <template v-slot:modal-footer>
            <loader v-if="isSaving" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isSaving"
            >
                Cancel
            </b-button>

            <b-button v-if="!isSaving" variant="primary" @click="save">
                Save
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import VetSelect from "@/module/commons/vet-select.vue";
import ClientCommunication from "@/services/client-communications";
import moment from "moment";
import Loader from "@/module/commons/loader";
import tinymce from "@tinymce/tinymce-vue";
import tinymceHelper from "@/module/commons/helper-tinymce"; // helper
import axios from "axios";
import TimePicker from "@/module/commons/time-picker";
import Editable from "@/components/elements/editable-textarea";

export default {
    components: {
        VetSelect,
        Loader,
        tinymce,
        TimePicker,
        Editable
    },
    props: {
        referer: {
            type: String
        },
        patientDetails: {
            type: Object
        }
    },
    data() {
        return {
            receiver: null,
            isSaving: false,
            isLoadingList: true,
            patient: null,
            patientOptions: [],
            clientId: null,
            date: null,
            time: null,
            timeFormat: "hh:mm A",
            datePickerOptions: {},
            type: 1, // phone
            typeOptions: [],
            message: "",
            dateTimeFormat: {},
            tinymceConfig: {
                ...tinymceHelper.core,
                toolbar: tinymceHelper.toolbar.communication,
                plugins: tinymceHelper.plugins,
                statusbar: true
            },
            formError: [],
            isEditMode: false,
            editRecordId: null
        };
    },
    computed: {
        patientId() {
            return this.patientDetails ? this.patientDetails.id : null;
        },
        patientPlaceholder() {
            return this.isLoadingList
                ? "Loading..."
                : "Patient Name (optional)";
        }
    },
    watch: {
        date(value) {
            if (value) {
                this.formError = this.formError.filter(err => err !== "date");
            }
        },
        time(value) {
            if (value) {
                this.formError = this.formError.filter(err => err !== "time");
            }
        }
    },
    methods: {
        async open(params = {}) {
            this.$refs.dialog.show();
            this.typeOptions = await ClientCommunication.getCommunicationTypes();

            if (params.action === "edit") {
                this.isEditMode = true;
                this.applyExistingRecord(params.record);
            } else {
                this.setDefaultReceiver();
                this.initDateTime();
            }
        },
        initDateTime() {
            this.date = moment.tz(this._timeZone).format(this.getDateFormat());
            this.time = moment.tz(this._timeZone);
        },
        setDefaultReceiver() {
            const primaryOwner = this.patientDetails.primary_owner;

            if (primaryOwner && primaryOwner[0]) {
                this.receiver = {
                    id: primaryOwner[0].id,
                    label: primaryOwner[0].label,
                    with_type: "client",
                    type: 0
                };
            }
        },
        close() {
            this.$refs.dialog.hide();
        },
        applyExistingRecord(record) {
            let receiver;
            let type;

            // trigger from history list
            if (this.referer === "history") {
                if (record.client) {
                    receiver = {
                        id: record.client.id,
                        label: record.client.label,
                        contact_type: "client"
                    };
                } else if (record.contact) {
                    receiver = {
                        id: record.contact.id,
                        label: record.contact.label,
                        contact_type: "contact"
                    };
                }

                let datetime = this._fromDateCarbon(record.date).setZone(
                    this._timeZone
                );
                this.time = datetime.toFormat("hh:mm a");
                this.date = datetime.toFormat(this._dateFormat);

                type = record.master_communication_type
                    ? record.master_communication_type.id
                    : null;

                this.message = record.content;
            }
            // trigger from communication list
            else {
                if (record.client && record.client[0]) {
                    receiver = {
                        id: record.client[0].id,
                        label: record.client[0].full_name,
                        contact_type: "client"
                    };
                } else if (record.contact && record.contact[0]) {
                    receiver = {
                        id: record.contact[0].id,
                        label: record.contact[0].full_name,
                        contact_type: "contact"
                    };
                }

                let momentDate = moment(record.date).tz(this._timeZone);
                this.date = momentDate.format(this.getDateFormat());
                this.time = momentDate.format(this.timeFormat);

                type = record.type ? record.type.id : null;
                this.message = record.raw_content;
            }

            this.editRecordId = record.id;
            this.receiver = receiver;
            this.type = type;
        },
        async save() {
            try {
                let hasError = false;
                this.formError = [];

                if (!this.receiver) {
                    this.formError.push("receiver");
                    hasError = true;
                }

                if (!this.date || this.date === "") {
                    this.formError.push("date");
                    hasError = true;
                }

                if (!this.time || this.time === "") {
                    this.formError.push("time");
                    hasError = true;
                }

                if (hasError) {
                    return;
                }

                if (this.message === "") {
                    throw new Error(" Message is required");
                }

                this.isSaving = true;

                let prefix = this.receiver.type === 1 ? "business" : "actor";
                let wid = `[${prefix}]${this.receiver.id}`;
                let time = this.time;
                if (moment.isMoment(time)) {
                    time = time.format("hh:mm A");
                }

                const formData = new FormData();
                formData.append("pid", this.patientId);
                formData.append("wid", wid);
                formData.append("with_type", this.receiver.with_type);
                formData.append("date", this.date);
                formData.append("time", time); // moment to string
                formData.append("type", this.type);
                formData.append("content", this.message);
                formData.append("is_system_generated", false);

                if (this.isEditMode) {
                    formData.append("id", this.editRecordId);
                }

                const res = await axios.post(
                    `${process.env.CI_APPS}patient/communication/save`,
                    formData
                );

                if (res.data.state === 0) {
                    this._message(
                        this.isEditMode ? "Edit saved." : "Communication saved."
                    );
                } else {
                    throw new Error("Error: Unable to save");
                }

                this.close();
                this.$emit("onsuccess");
            } catch (e) {
                this._message(e.message, "danger");
                this.isSaving = false;
            }
        }
    }
};
</script>
